/**
 * Email store — thread slice.
 */
import {
  upsertThreadItem,
  removeThreadItem,
  confirmOptimisticComment,
  stripOptimisticComments,
  buildThreadPath,
  buildAuditEventsPath,
  buildThreadStatePath,
  buildThreadStatusPath,
  buildThreadResponsiblePath,
  buildThreadFolderPath,
  buildTaskLinkPath,
  buildTaskLinksPath,
  buildTaskLinksSearchPath,
  buildTaskFormPath,
  buildTasksPath,
  buildTaskTagsPath,
  buildPersonGroupsPath,
  buildEmailApiPath,
} from '@tshmanager/email-shared'
import { emailApiBase, normalizeThreadDetail, normalizeListRow, normalizeComment } from './api.js'

export function attachThread(ctx) {
  // ── JSON fetch: thread ────────────────────────────────────────────────────

  ctx.THREAD_PREFETCH_TTL_MS = 60_000
  ctx.THREAD_PREFETCH_MAX = 8
  ctx.threadPrefetchCache = new Map()
  ctx.prefetchTimer = null
  ctx.prefetchTargetId = null

  ctx._applyThreadData = function _applyThreadData(data) {
    ctx.thread.value = data
    const emailItems = (data.items || []).filter(i => i.type === 'email')
    if (emailItems.length > 0) {
      const expanded = {}
      expanded[emailItems[0].email.id] = true
      ctx.expandedMessages.value = expanded
    }
    ctx.commentFormVisible.value = false
    ctx.auditLogVisible.value = false
    ctx.auditEvents.value = []
  }

  ctx._getThreadPrefetch = function _getThreadPrefetch(emailId) {
    const entry = ctx.threadPrefetchCache.get(Number(emailId))
    if (!entry) return null
    if (Date.now() - entry.at > ctx.THREAD_PREFETCH_TTL_MS) {
      ctx.threadPrefetchCache.delete(Number(emailId))
      return null
    }
    return entry.data
  }

  ctx._cacheThreadPrefetch = function _cacheThreadPrefetch(emailId, data) {
    const id = Number(emailId)
    if (ctx.threadPrefetchCache.size >= ctx.THREAD_PREFETCH_MAX) {
      const oldest = ctx.threadPrefetchCache.keys().next().value
      ctx.threadPrefetchCache.delete(oldest)
    }
    ctx.threadPrefetchCache.set(id, { data, at: Date.now() })
  }

  ctx.cancelThreadPrefetch = function cancelThreadPrefetch() {
    clearTimeout(ctx.prefetchTimer)
    ctx.prefetchTimer = null
    ctx.prefetchTargetId = null
  }

  // Drafts folder rows use EmailDraft ids — never hit /emails/threads/:id.
  ctx._isDraftListRow = function _isDraftListRow(emailId) {
    if (ctx.activeFolder.value === 'drafts') return true
    const row = ctx.emails.value.find(e => Number(e.id) === Number(emailId))
    return !!row?.is_draft
  }

  ctx.scheduleThreadPrefetch = function scheduleThreadPrefetch(emailId) {
    clearTimeout(ctx.prefetchTimer)
    const id = Number(emailId)
    if (!id || id === ctx.currentThreadId.value || ctx._getThreadPrefetch(id)) return
    if (ctx._isDraftListRow(id)) return

    ctx.prefetchTargetId = id
    ctx.prefetchTimer = setTimeout(() => {
      ctx.prefetchTimer = null
      if (ctx.prefetchTargetId === id) ctx.prefetchThread(id)
    }, 120)
  }

  ctx.prefetchThread = async function prefetchThread(emailId) {
    const id = Number(emailId)
    if (!id || id === ctx.currentThreadId.value || ctx._getThreadPrefetch(id) || ctx.prefetchTargetId !== id) return
    if (ctx._isDraftListRow(id)) return

    try {
      const data = normalizeThreadDetail(await ctx.apiGet(buildThreadPath(emailApiBase(), id), {
        headers: { 'X-Sec-Purpose': 'prefetch' },
      }))
      if (ctx.prefetchTargetId === id) ctx._cacheThreadPrefetch(id, data)
    } catch (_) {
      // Silent — prefetch is best-effort
    }
  }

  ctx.fetchThread = async function fetchThread(emailId, { bypassCache = false } = {}) {
    if (!emailId) return
    const id = Number(emailId)
    if (ctx._isDraftListRow(id)) {
      ctx.openDraftById(id)
      return
    }
    ctx.cancelThreadPrefetch()

    // Optimistic: mark unread → read and apply shared count transition (inbox or custom folder).
    const emailRow = ctx.emails.value.find(e => e.id === id)
    const before = emailRow ? ctx._threadFacts(emailRow) : null
    const wasUnreadOpenable = before && before.unread && !before.archived && !before.trashed
    if (wasUnreadOpenable && emailRow) {
      const after = { ...before, unread: false }
      ctx._applyCountTransition(before, after)
      ctx._updateEmailRow({ ...emailRow, read: true })
    }

    const cached = bypassCache ? null : ctx._getThreadPrefetch(id)
    if (cached) {
      ctx.batch(() => {
        ctx._applyThreadData(cached)
        ctx.threadLoading.value = false
      })
    } else {
      ctx.threadLoading.value = true
    }

    // Explicit "opened" signal, independent of whether the GET below is
    // served fresh or straight from the browser's HTTP cache (a prior
    // hover-prefetch response sharing the exact same URL) — always fired so
    // the server-side mark-as-read + thread_updated broadcast never depend
    // on caching behavior.
    ctx.ackThreadOpened(id)

    try {
      const headers = bypassCache
        ? { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
        : undefined
      const data = normalizeThreadDetail(await ctx.apiGet(buildThreadPath(emailApiBase(), id), { headers }))
      ctx.batch(() => {
        ctx._applyThreadData(data)
        ctx.threadLoading.value = false
      })
      ctx.threadPrefetchCache.delete(id)
    } catch (err) {
      // Revert optimistic updates if request failed
      if (wasUnreadOpenable && emailRow && before) {
        ctx._applyCountTransition({ ...before, unread: false }, before)
        ctx._updateEmailRow({ ...emailRow, read: false })
      }
      console.error('[emailStore] fetchThread error:', err)
      ctx.threadLoading.value = false
    }
  }


  ctx._webItemsToShared = function _webItemsToShared(items) {
    return (items || []).map(i => {
      if (i.type === 'email' && i.email) {
        return { type: 'email', id: i.email.id, at: i.at, data: i.email }
      }
      if (i.type === 'comment' && i.comment) {
        const c = i.comment
        return {
          type: 'comment',
          id: c.id,
          at: i.at || c.created_at,
          data: {
            id: c.id,
            body: c.body,
            created_at: c.created_at,
            person: {
              id: c.person_id || c.person?.id,
              name: c.person_name || c.person?.name,
              avatar_url: c.avatar_url || c.person?.avatar_url || null,
            },
            is_own: c.is_own,
            person_name: c.person_name,
            person_id: c.person_id,
            avatar_url: c.avatar_url,
            email_id: c.email_id ?? null,
          },
        }
      }
      if (i.type === 'draft' && i.draft) {
        return { type: 'draft', id: i.draft.id, at: i.at || i.draft.updated_at, data: i.draft }
      }
      return null
    }).filter(Boolean)
  }

  ctx._sharedToWebItems = function _sharedToWebItems(items) {
    return (items || []).map(i => {
      if (i.type === 'email') {
        return { type: 'email', at: i.at, email: i.data, comment: null, draft: null }
      }
      if (i.type === 'comment') {
        const d = i.data || {}
        return {
          type: 'comment',
          at: i.at,
          email: null,
          comment: {
            id: d.id,
            body: d.body,
            created_at: d.created_at || i.at,
            person_name: d.person_name || d.person?.name,
            person_id: d.person_id || d.person?.id,
            avatar_url: d.avatar_url || d.person?.avatar_url || null,
            is_own: d.is_own,
            email_id: d.email_id ?? null,
          },
          draft: null,
        }
      }
      return { type: 'draft', at: i.at, email: null, comment: null, draft: i.data }
    })
  }

  ctx._enrichCableItem = function _enrichCableItem(item) {
    if (!item || item.type !== 'email' || !item.data) return item
    const data = { ...item.data }
    if (!data.from_name) {
      const addr = data.from_address
      if (addr && typeof addr === 'object') {
        data.from_name = addr.name || addr.email || addr.address || ''
      } else if (typeof addr === 'string') {
        data.from_name = addr
      }
    }
    return { ...item, data }
  }

  ctx._patchOpenThreadItems = function _patchOpenThreadItems(mutateShared) {
    if (!ctx.thread.value?.items) return
    const shared = ctx._webItemsToShared(ctx.thread.value.items)
    ctx.thread.value = { ...ctx.thread.value, items: ctx._sharedToWebItems(mutateShared(shared)) }
  }


  // ── Thread UI mutators ────────────────────────────────────────────────────

  ctx.setThreadLoading = function setThreadLoading(loading) {
    ctx.threadLoading.value = loading
  }

  ctx.showCommentForm = function showCommentForm() {
    ctx.commentFormVisible.value = true
  }

  ctx.hideCommentForm = function hideCommentForm() {
    ctx.commentFormVisible.value = false
  }

  ctx.toggleAuditLog = function toggleAuditLog(visible) {
    ctx.auditLogVisible.value = visible
    if (visible && ctx.thread.value && ctx.auditEvents.value.length === 0) {
      ctx.loadAuditEvents()
    }
  }

  ctx.toggleMessageExpanded = function toggleMessageExpanded(emailId) {
    const id = Number(emailId)
    ctx.expandedMessages.value = {
      ...ctx.expandedMessages.value,
      [id]: !ctx.expandedMessages.value[id],
    }
  }

  ctx.expandMessage = function expandMessage(emailId) {
    ctx.expandedMessages.value = { ...ctx.expandedMessages.value, [Number(emailId)]: true }
  }

  ctx.collapseMessage = function collapseMessage(emailId) {
    ctx.expandedMessages.value = { ...ctx.expandedMessages.value, [Number(emailId)]: false }
  }

  ctx.loadAuditEvents = async function loadAuditEvents() {
    if (!ctx.currentThreadId.value) return
    ctx.auditEventsLoading.value = true
    try {
      const data = await ctx.apiGet(
        buildAuditEventsPath(emailApiBase(), ctx.currentThreadId.value)
      )
      ctx.auditEvents.value = data.events || []
    } catch (err) {
      console.error('[emailStore] loadAuditEvents error:', err)
    } finally {
      ctx.auditEventsLoading.value = false
    }
  }

  // ── Mutations (PATCH/POST with optimistic update) ─────────────────────────

  ctx.updateEmailState = async function updateEmailState(emailId, actionType) {
    const id = Number(emailId)
    // Draft rows are not threads. Archive/trash/spam discard the EmailDraft
    // (keyboard + drag). Other actions have no draft equivalent.
    if (ctx._isDraftListRow(id)) {
      if (actionType === 'archive' || actionType === 'trash' || actionType === 'spam') {
        await ctx.bulkUpdate([id], actionType)
      }
      return
    }
    // Determine the email's current state before the request for accurate delta.
    const emailRow = ctx.emails.value.find(e => Number(e.id) === id)
    const rowForFacts = emailRow || (ctx.thread.value ? {
      folder_id: ctx.thread.value.folder_id ?? null,
      read: ctx.thread.value.read,
      starred: ctx.thread.value.starred,
      archived: !!(ctx.thread.value.archived || ctx.thread.value.archived_at),
      trashed: !!(ctx.thread.value.trashed || ctx.thread.value.trashed_at),
      spam: !!(ctx.thread.value.spam || ctx.thread.value.spam_at),
      archived_at: ctx.thread.value.archived_at ?? null,
      trashed_at: ctx.thread.value.trashed_at ?? null,
      spam_at: ctx.thread.value.spam_at ?? null,
    } : null)
    const before = ctx._threadFacts(rowForFacts)

    // Build after-facts from the action up front (shared by optimistic + success paths).
    const after = { ...before }
    switch (actionType) {
      case 'archive':
        after.archived = true
        after.trashed = false
        after.spam = false
        break
      case 'trash':
        after.trashed = true
        after.spam = false
        break
      case 'spam':
        after.spam = true
        after.archived = false
        after.trashed = false
        after.emailFolderId = null
        break
      case 'not_spam':
        after.spam = false
        break
      case 'restore':
        after.archived = false
        after.trashed = false
        after.spam = false
        break
      case 'star':
        after.starred = !before.starred
        break
      case 'unread':
        after.unread = true
        break
      case 'read':
        after.unread = false
        break
    }

    const leavesView = actionType === 'archive' || actionType === 'trash' || actionType === 'spam' || actionType === 'delete'

    // Optimistic UI: star in-place; archive/trash leave the current list immediately (DnD).
    if (actionType === 'star' && emailRow) {
      ctx._updateEmailRow({ ...emailRow, starred: after.starred })
      ctx._applyCountTransition(before, after)
    } else if (leavesView) {
      ctx._applyCountTransition(before, after, { skipListMeta: true })
      ctx._removeEmailRow(id)
      if (Number(ctx.currentThreadId.value) === id) ctx.clearCurrentThread()
    }

    try {
      const data = await ctx.apiPatch(
        buildThreadStatePath(emailApiBase(), id),
        { action_type: actionType }
      )

      if (leavesView) {
        // Already removed optimistically; keep badges authoritative.
      } else if (data.removed) {
        ctx._applyCountTransition(before, after, { skipListMeta: true })
        ctx._removeEmailRow(id)
        if (Number(ctx.currentThreadId.value) === id) ctx.clearCurrentThread()
      } else {
        if (actionType !== 'star') ctx._applyCountTransition(before, after)
        const updated = data.thread || data.email
        if (updated) {
          const row = normalizeListRow(updated)
          ctx._updateEmailRow(row)
          if (Number(ctx.thread.value?.id) === id) {
            ctx.thread.value = { ...ctx.thread.value, ...row }
          }
        }
      }

      // Archive/trash/restore change which folder badges should show — refresh
      // so custom-folder counts drop when an email leaves a custom folder.
      if (['archive', 'trash', 'spam', 'not_spam', 'restore', 'read', 'unread'].includes(actionType)) {
        ctx.fetchSidebarCounts()
      }
    } catch (err) {
      // Revert optimistic star update on server error
      if (actionType === 'star' && emailRow) {
        ctx._applyCountTransition(after, before)
        ctx._updateEmailRow(emailRow)
      } else if (leavesView) {
        ctx.fetchEmails(1)
        ctx.fetchSidebarCounts()
      }
      console.error('[emailStore] updateEmailState error:', err)
    }
  }

  ctx.changeEmailStatus = async function changeEmailStatus(emailId, status) {
    try {
      const data = await ctx.apiPatch(
        buildThreadStatusPath(emailApiBase(), emailId),
        { status }
      )
      const updated = data.thread || data.email
      if (updated) ctx._updateEmailRow(normalizeListRow(updated))
      if (ctx.thread.value?.id === emailId) {
        ctx.thread.value = { ...ctx.thread.value, status }
      }
    } catch (err) {
      console.error('[emailStore] changeEmailStatus error:', err)
    }
  }

  ctx.assignResponsible = async function assignResponsible(emailId, responsibleId) {
    try {
      const data = await ctx.apiPatch(
        buildThreadResponsiblePath(emailApiBase(), emailId),
        { person_id: responsibleId || null }
      )
      const updated = data.thread || data.email
      if (updated) ctx._updateEmailRow(normalizeListRow(updated))

      const people = ctx.peopleWithUser.value || []
      const person = responsibleId
        ? people.find(p => p.id === Number(responsibleId))
        : null
      const responsible = person
        ? { id: person.id, name: person.name, avatar_url: person.avatar_url }
        : null

      if (ctx.thread.value?.id === emailId) {
        ctx.thread.value = {
          ...ctx.thread.value,
          responsible,
          responsible_person: responsible,
        }
      }
    } catch (err) {
      console.error('[emailStore] assignResponsible error:', err)
    }
  }

  ctx.moveToFolder = async function moveToFolder(emailId, folderId) {
    const id = Number(emailId)
    const emailRow = ctx.emails.value.find(e => Number(e.id) === id)
    const before = ctx._threadFacts(emailRow)
    const targetFolderId = folderId != null && folderId !== '' ? Number(folderId) : null
    const targetFolderIdOrNull = Number.isFinite(targetFolderId) ? targetFolderId : null

    if (targetFolderIdOrNull) {
      const folder = (ctx.emailFolders.value || []).find(f => Number(f.id) === targetFolderIdOrNull)
      // Only compare when the dragged row is known — never fall back to the open thread
      // (that mailbox can differ from the dragged email and falsely block the move).
      const emailMailboxId = emailRow?.mailbox_id
      if (
        folder &&
        folder.mailbox_id != null &&
        emailMailboxId != null &&
        Number(folder.mailbox_id) !== Number(emailMailboxId)
      ) {
        console.warn('[emailStore] moveToFolder skipped: folder mailbox mismatch', {
          folderId: targetFolderIdOrNull,
          folderMailboxId: folder.mailbox_id,
          emailMailboxId,
        })
        return
      }
    }

    const after = {
      ...before,
      emailFolderId: targetFolderIdOrNull,
      archived: false,
      trashed: false,
    }
    // Optimistic leave-view so the source list updates immediately on DnD.
    ctx._applyCountTransition(before, after, { skipListMeta: true })
    ctx._removeEmailRow(id)
    if (Number(ctx.currentThreadId.value) === id) ctx.clearCurrentThread()

    try {
      await ctx.apiPatch(
        buildThreadFolderPath(emailApiBase(), id),
        { email_folder_id: targetFolderIdOrNull || '' }
      )
      // Authoritative refresh — ActionCable may be delayed / unscoped; keep badges in sync.
      ctx.fetchSidebarCounts()
    } catch (err) {
      console.error('[emailStore] moveToFolder error:', err)
      // Best-effort resync — row was removed optimistically.
      ctx.fetchEmails(1)
      ctx.fetchSidebarCounts()
    }
  }

  /**
   * Restore archived/trashed/spam state and/or clear custom folder → inbox in one
   * count transition (used by DnD onto inbox). Avoids double restore+move patches.
   */
  ctx.restoreToInbox = async function restoreToInbox(emailId) {
    const id = Number(emailId)
    const emailRow = ctx.emails.value.find(e => Number(e.id) === id)
    const before = ctx._threadFacts(emailRow)
    const activeIsCustomFolder = typeof ctx.activeFolder.value === 'string'
      && ctx.activeFolder.value.startsWith('folder_')
    const needsRestore = before.archived || before.trashed || before.spam
    // If the row is missing from the list signal, still clear folder when viewing a custom folder.
    const needsMove = before.emailFolderId != null || (!emailRow && activeIsCustomFolder)

    if (!needsRestore && !needsMove) return

    const after = {
      ...before,
      archived: false,
      trashed: false,
      spam: false,
      emailFolderId: null,
    }
    // Optimistic leave-view so DnD removes the row immediately.
    ctx._applyCountTransition(before, after, { skipListMeta: true })
    ctx._removeEmailRow(id)
    if (Number(ctx.currentThreadId.value) === id) ctx.clearCurrentThread()

    try {
      if (needsRestore) {
        const actionType = before.spam && !before.archived && !before.trashed ? 'not_spam' : 'restore'
        await ctx.apiPatch(
          buildThreadStatePath(emailApiBase(), id),
          { action_type: actionType }
        )
      }
      if (needsMove || needsRestore) {
        // Clear folder id after restore (restore alone does not clear email_folder_id).
        await ctx.apiPatch(
          buildThreadFolderPath(emailApiBase(), id),
          { email_folder_id: '' }
        )
      }
      ctx.fetchSidebarCounts()
    } catch (err) {
      console.error('[emailStore] restoreToInbox error:', err)
      ctx.fetchEmails(1)
      ctx.fetchSidebarCounts()
    }
  }


  ctx.postComment = async function postComment(emailId, body) {
    const text = (body || '').trim()
    if (!text || !ctx.thread.value) return

    const tempId = -Date.now()
    const person = ctx._currentPersonBrief()
    const nowIso = new Date().toISOString()
    const optimistic = {
      type: 'comment',
      id: tempId,
      at: nowIso,
      data: {
        id: tempId,
        body: text,
        created_at: nowIso,
        person: {
          id: person?.id ?? null,
          name: person?.name || '',
          avatar_url: person?.avatar_url || null,
        },
        is_own: true,
        person_name: person?.name || '',
        person_id: person?.id ?? null,
        avatar_url: person?.avatar_url || null,
        pending: true,
      },
    }
    ctx._patchOpenThreadItems(items => upsertThreadItem(items, optimistic))
    ctx.hideCommentForm()

    try {
      const data = await ctx.apiPost(buildEmailApiPath(emailApiBase(), 'comments'), {
        email_id: emailId,
        email_comment: { body: text },
      })
      if (!data?.comment || !ctx.thread.value) return
      const c = normalizeComment(data.comment, emailId)
      const real = {
        type: 'comment',
        id: c.id,
        at: c.created_at,
        data: {
          ...c,
          person: {
            id: c.person_id,
            name: c.person_name,
            avatar_url: c.avatar_url,
          },
          is_own: c.is_own !== false,
        },
      }
      ctx._patchOpenThreadItems(items => confirmOptimisticComment(items, tempId, real))
    } catch (err) {
      ctx._patchOpenThreadItems(items => removeThreadItem(items, 'comment', tempId))
      console.error('[emailStore] postComment error:', err)
    }
  }

  ctx._currentPersonBrief = function _currentPersonBrief() {
    const id = Number(ctx.currentPersonId?.value)
    if (!id) return null
    const fromList = (ctx.peopleWithUser.value || []).find(p => Number(p.id) === id)
    return {
      id,
      name: fromList?.name || '',
      avatar_url: fromList?.avatar_url || null,
    }
  }

  ctx.deleteComment = async function deleteComment(commentId) {
    // v1 scopes the comment to its owning email, so email_id must travel along.
    const owner = (ctx.thread.value?.items || []).find(
      i => i.type === 'comment' && Number(i.comment?.id) === Number(commentId)
    )
    const emailId = owner?.comment?.email_id ?? ctx.thread.value?.id
    try {
      await ctx.apiDelete(
        buildEmailApiPath(emailApiBase(), 'comments', commentId) + `?email_id=${emailId}`
      )
      if (ctx.thread.value) {
        ctx.thread.value = {
          ...ctx.thread.value,
          items: ctx.thread.value.items.filter(
            i => !(i.type === 'comment' && i.comment?.id === commentId)
          ),
        }
      }
    } catch (err) {
      console.error('[emailStore] deleteComment error:', err)
    }
  }

  /**
   * task_links / tasks responses carry only the `tasks` slice of the infobar,
   * so merge it into the thread instead of replacing the whole infobar (which
   * would drop the people / building complex sections).
   */
  ctx.applyTaskInfobar = function applyTaskInfobar(infobar) {
    const thread = ctx.thread.value
    if (!thread || !infobar?.tasks) return
    ctx.thread.value = {
      ...thread,
      infobar: { ...thread.infobar, tasks: infobar.tasks },
    }
  }

  ctx.unlinkTask = async function unlinkTask(connectionId) {
    try {
      const data = await ctx.apiDelete(buildTaskLinkPath(emailApiBase(), connectionId))
      if (data?.infobar) {
        ctx.applyTaskInfobar(data.infobar)
      } else if (ctx.thread.value) {
        ctx.thread.value = {
          ...ctx.thread.value,
          infobar: {
            ...ctx.thread.value.infobar,
            tasks: (ctx.thread.value.infobar?.tasks || []).filter(
              t => t.connection_id !== connectionId
            ),
          },
        }
      }
    } catch (err) {
      console.error('[emailStore] unlinkTask error:', err)
    }
  }

  // ── Task from email: create form + link picker ─────────────────────────────

  ctx.fetchTaskForm = function fetchTaskForm(emailId) {
    return ctx.apiGet(buildTaskFormPath(emailApiBase(), Number(emailId)))
  }

  ctx.fetchTaskTags = function fetchTaskTags(q) {
    return ctx.apiGet(buildTaskTagsPath(emailApiBase(), q ? { q } : undefined))
  }

  ctx.fetchTaskPersonGroups = function fetchTaskPersonGroups(opts = {}) {
    return ctx.apiGet(buildPersonGroupsPath(emailApiBase(), opts))
  }

  ctx.createTaskFromEmail = async function createTaskFromEmail(emailId, task) {
    const data = await ctx.apiPost(buildTasksPath(emailApiBase()), {
      email_id: Number(emailId),
      task,
    })
    ctx.applyTaskInfobar(data?.infobar)
    return data
  }

  ctx.searchLinkableTasks = function searchLinkableTasks(emailId, filters = {}) {
    return ctx.apiGet(buildTaskLinksSearchPath(emailApiBase(), {
      email_id: Number(emailId),
      ...filters,
    }))
  }

  ctx.linkTaskToEmail = async function linkTaskToEmail(emailId, taskId) {
    const data = await ctx.apiPost(buildTaskLinksPath(emailApiBase()), {
      email_id: Number(emailId),
      task_id: Number(taskId),
    })
    ctx.applyTaskInfobar(data?.infobar)
    return data
  }

}
